/**
 * The brief's headline, built from data so it reads the same on every case:
 * the client (its playbook), the report type the case runs, the case's own
 * subject and how many others, and when it's due — or, once the case is
 * past its delivery, where it ended.
 */

import type { BriefSubject, WorkbenchHeader } from '@/types/workbench';

const DAY_MS = 86_400_000;

/** Stages past which a due date no longer says anything, by name. */
const ENDED: Record<string, string> = {
  in_qa: 'in QA',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
};

function startOfDay(at: Date): number {
  return new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
}

/** "due 24 Oct (in 2 days)", "due 20 Oct (overdue by 2 days)" — or where the
 * case ended. */
export function dueText(dueAt: string | null, stage: string, now: Date): string | null {
  if (stage in ENDED) {
    return ENDED[stage];
  }
  if (!dueAt) {
    return null;
  }
  const due = new Date(dueAt);
  const days = Math.round((startOfDay(due) - startOfDay(now)) / DAY_MS);
  let when: string;
  if (days > 1) {
    when = `in ${days} days`;
  } else if (days === 1) {
    when = 'tomorrow';
  } else if (days === 0) {
    when = 'today';
  } else {
    when = `overdue by ${-days} day${days === -1 ? '' : 's'}`;
  }
  const date = due.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `due ${date} (${when})`;
}

/** "Northwind · 2B · Northwind Trading Ltd +3 · due 24 Oct (in 2 days)",
 * leaving out whatever the case doesn't have. */
export function briefHeadline(
  header: WorkbenchHeader | null,
  subjects: readonly BriefSubject[],
  reportTypeName: string | null = null,
  now: Date = new Date()
): string | null {
  if (!header) {
    return null;
  }
  const parts: string[] = [];
  if (header.playbook) {
    parts.push(header.playbook.name);
    const type = reportTypeName ?? header.playbook.reportType;
    if (type) {
      parts.push(type);
    }
  }
  const main = subjects.find((s) => s.main) ?? subjects[0];
  if (main) {
    parts.push(subjects.length > 1 ? `${main.name} +${subjects.length - 1}` : main.name);
  }
  const due = dueText(header.due.dueAt, header.lifecycleStage, now);
  if (due) {
    parts.push(due);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}
